import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  FaCheck,
  FaEnvelope,
  FaBox,
  FaShippingFast,
  FaClock,
  FaCalendarAlt,
  FaCreditCard,
  FaMoneyBillWave,
  FaBuilding,
  FaFileInvoice,
} from "react-icons/fa";
import SmallClouds from "../../components/utils/SmallClouds";
import {
  PageWrapper,
  Container,
  SuccessContainer,
  SuccessIcon,
  SuccessTitle,
  SuccessSubtitle,
  InfoGrid,
  InfoBox,
  InfoHeader,
  InfoTitle,
  InfoText,
  RedirectNotice,
  RedirectText,
  OrderNumber,
} from "./PaymentSuccess.styles";
import { useCart } from "../../store/CartContext";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [count, setCount] = useState(10);
  const { clearCart } = useCart();

  const orderNumber = searchParams.get("order");
  const orderType = searchParams.get("type") || "purchase"; // 'workshop', 'purchase', or 'mixed'
  const paymentMethod = searchParams.get("method") || "card"; // 'card', 'bank', 'cash', 'pickup'

  useEffect(() => {
    clearCart();
  }, []);

  useEffect(() => {
    if (count === 0) return;
    const timer = setTimeout(() => setCount(count - 1), 1000);
    return () => clearTimeout(timer);
  }, [count]);

  // ✅ Auto redirect based on order type
  useEffect(() => {
    if (count === 0) {
      const redirectUrl =
        orderType === "workshop"
          ? "/korisnicki-panel/moje-radionice"
          : "/korisnicki-panel/moji-setovi";

      window.location.href = redirectUrl;
    }
  }, [count, orderType]);

  const getPaymentMethodInfo = () => {
    switch (paymentMethod) {
      case "bank":
        return {
          icon: FaFileInvoice,
          title: "Plaćanje uplatnicom",
          text: "Molimo izvršite uplatu prema uplatnici. Setovi će biti aktivirani nakon potvrde uplate (1-2 radna dana).",
          statusNote: "Čeka se uplata",
        };
      case "cash":
        return {
          icon: FaMoneyBillWave,
          title: "Plaćanje pouzećem",
          text: "Platite kuriru prilikom preuzimanja. Setovi će biti aktivirani nakon potvrde uplate.",
          statusNote: "Čeka se potvrda uplate",
        };
      case "pickup":
        return {
          icon: FaBuilding,
          title: "Lično preuzimanje",
          text: 'Kontaktiraćemo vas za dogovor oko preuzimanja u vrtiću "Dobra Vila". Setovi će biti aktivirani nakon potvrde uplate.',
          statusNote: "Čeka se potvrda uplate",
        };
      case "card":
      default:
        return {
          icon: FaCreditCard,
          title: "Plaćanje karticom",
          text: "Plaćanje je uspešno obrađeno. Setovi su odmah dostupni u vašem nalogu!",
          statusNote: "Plaćeno - odmah dostupno",
        };
    }
  };

  const getContentByOrderType = () => {
    const paymentInfo = getPaymentMethodInfo();

    if (orderType === "workshop") {
      return {
        title: "Registracija uspešna!",
        subtitle:
          paymentMethod === "bank"
            ? "Vaša registracija za radionicu je primljena. Molimo izvršite uplatu prema uplatnici da bi se registracija potvrdila."
            : "Hvala vam na poverenju! Vaša registracija za radionicu je uspešno potvrđena.",
        deliveryInfo: {
          icon: FaCalendarAlt,
          title: "Radionica",
          text:
            paymentMethod === "bank"
              ? "Detalje o radionici ćete videti u panelu nakon potvrde uplate"
              : "Detalje o radionici možete videti u korisničkom panelu",
        },
        paymentInfo,
        redirectText: "Automatski ćete biti preusmeren na Moje radionice",
        redirectPath: "/korisnicki-panel/moje-radionice",
      };
    } else if (orderType === "mixed") {
      return {
        title:
          paymentMethod === "bank" ||
          paymentMethod === "cash" ||
          paymentMethod === "pickup"
            ? "Narudžbina primljena!"
            : "Plaćanje uspešno!",
        subtitle:
          paymentMethod === "bank"
            ? "Vaša narudžbina je primljena. Molimo izvršite uplatu prema uplatnici. Setovi i radionice će biti aktivirani nakon potvrde uplate."
            : paymentMethod === "cash"
              ? "Hvala vam na poverenju! Vaša narudžbina je primljena. Setovi i radionice će biti aktivirani nakon potvrde uplate prilikom dostave."
              : paymentMethod === "pickup"
                ? "Hvala vam na poverenju! Vaša narudžbina je primljena. Setovi i radionice će biti aktivirani nakon potvrde uplate prilikom preuzimanja."
                : "Hvala vam na poverenju! Vaše plaćanje je uspešno obrađeno. Setovi i radionice su odmah dostupni!",
        deliveryInfo: {
          icon: FaShippingFast,
          title: "Dostava i radionice",
          text:
            paymentMethod === "bank"
              ? "Setovi će biti isporučeni nakon potvrde uplate (1-2 radna dana)"
              : paymentMethod === "pickup"
                ? "Dogovorićemo termin preuzimanja telefonom"
                : "Setovi će biti isporučeni u roku od 1-2 radna dana",
        },
        paymentInfo,
        redirectText: "Automatski ćete biti preusmeren na korisnički panel",
        redirectPath: "/korisnicki-panel/moji-setovi",
      };
    } else {
      // Default: purchase
      return {
        title:
          paymentMethod === "bank" ||
          paymentMethod === "cash" ||
          paymentMethod === "pickup"
            ? "Narudžbina primljena!"
            : "Plaćanje uspešno!",
        subtitle:
          paymentMethod === "bank"
            ? "Vaša narudžbina je primljena. Molimo izvršite uplatu prema uplatnici. Setovi će biti aktivirani nakon potvrde uplate (1-2 radna dana)."
            : paymentMethod === "cash"
              ? "Hvala vam na poverenju! Vaša narudžbina je primljena. Setovi će biti aktivirani nakon potvrde uplate prilikom dostave."
              : paymentMethod === "pickup"
                ? "Hvala vam na poverenju! Vaša narudžbina je primljena. Setovi će biti aktivirani nakon potvrde uplate prilikom preuzimanja."
                : "Hvala vam na poverenju! Vaše plaćanje je uspešno obrađeno. Setovi su odmah dostupni u vašem nalogu!",
        deliveryInfo: {
          icon: FaShippingFast,
          title: "Dostava",
          text:
            paymentMethod === "bank"
              ? "Narudžbina će biti poslata nakon potvrde uplate (1-2 radna dana)"
              : paymentMethod === "pickup"
                ? "Dogovorićemo termin preuzimanja telefonom"
                : "Narudžbina će biti obrađena u roku od 1-2 radna dana",
        },
        paymentInfo,
        redirectText: "Automatski ćete biti preusmeren na Moji setovi",
        redirectPath: "/korisnicki-panel/moji-setovi",
      };
    }
  };

  const content = getContentByOrderType();

  return (
    <PageWrapper>
      <SmallClouds experiences />

      <Container>
        <SuccessContainer>
          <SuccessIcon>
            <FaCheck />
          </SuccessIcon>

          <SuccessTitle>{content.title}</SuccessTitle>

          {orderNumber && <OrderNumber>Narudžbina #{orderNumber}</OrderNumber>}

          <SuccessSubtitle>{content.subtitle}</SuccessSubtitle>

          <InfoGrid>
            <InfoBox>
              <InfoHeader>
                <FaEnvelope />
                <InfoTitle>Email potvrda</InfoTitle>
              </InfoHeader>
              <InfoText>
                Potvrda narudžbine
                {paymentMethod === "bank" && " i instrukcije za plaćanje"} je
                poslata na vašu email adresu
              </InfoText>
            </InfoBox>

            <InfoBox>
              <InfoHeader>
                <content.paymentInfo.icon />
                <InfoTitle>{content.paymentInfo.title}</InfoTitle>
              </InfoHeader>
              <InfoText>{content.paymentInfo.text}</InfoText>
            </InfoBox>

            <InfoBox>
              <InfoHeader>
                <FaBox />
                <InfoTitle>Status</InfoTitle>
              </InfoHeader>
              <InfoText>
                {content.paymentInfo.statusNote}
                <br />
                Možete pratiti status u korisničkom panelu
              </InfoText>
            </InfoBox>
          </InfoGrid>

          <RedirectNotice>
            <RedirectText>
              <FaClock />
              {content.redirectText} za {count} sekunde...
            </RedirectText>
          </RedirectNotice>
        </SuccessContainer>
      </Container>
    </PageWrapper>
  );
}
